import { z } from 'zod';

export const RedactionStyleLabelSchema = z.literal('redaction_style');
export const REDACTION_STYLE_CHAR_LIMIT = 20_000;
export const ActStructurePatchReviewActionSchema = z.enum(['Reessayer', 'Faire sans le patch']);
export const RedactionStyleBlockSchema = z.object({
  label: RedactionStyleLabelSchema,
  content: z.string().min(1).max(REDACTION_STYLE_CHAR_LIMIT),
});

/**
 * Niveaux de titre admis dans le contrat de structure d'un acte.
 *
 * POURQUOI six et pas sept : `Hector-H7` nommerait un style Word qui n'existe
 * nulle part — l'add-in en définit six, `CANVAS_ALLOWED_STYLES` en autorise
 * six, `HECTOR_HEADING_STYLE_LEVELS` en mappe six. Six est la frontière
 * physique, pas une préférence.
 *
 * POURQUOI plus quatre (corrigé 2026-08-20) : l'énumération s'arrêtait à
 * `Hector-H4` alors que tout le reste de la plateforme allait déjà jusqu'à
 * six, et que `structuralParser.ts` fait saturer les titres markdown trop
 * profonds sur le sixième niveau (`Math.min(h[1].length, 6)`). Un modèle qui
 * proposait un cinquième niveau — comportement raisonnable quand la plume du
 * cabinet décrit une hiérarchie A / 1 / a / (i) — faisait échouer la
 * validation Zod, donc la construction de la source de structure, donc la
 * compilation du patch : le levier structure s'éteignait ENTIÈREMENT et
 * SILENCIEUSEMENT pour le cabinet (`status: compile_failed`, mesuré le
 * 2026-08-20 sur un vrai lancement d'acte). Une préférence de rédaction
 * — un acte se lit rarement au-delà de I / A / 1 / a — n'a pas à être une
 * contrainte dure de schéma : sa place est dans le prompt, où un dépassement
 * produit un acte trop profond plutôt qu'une fonctionnalité morte.
 */
export const HectorTagSchema = z.enum([
  'Hector-H1', 'Hector-H2', 'Hector-H3', 'Hector-H4', 'Hector-H5', 'Hector-H6',
]);
export const ActStructureSourceSchema = z.enum(['act_reference_analysis', 'manual', 'legacy_backfill']);
export const CompileStatusSchema = z.enum(['missing', 'compiling', 'completed', 'failed']);

export const ReferenceActTextAlignmentSchema = z.enum(['left', 'center', 'right', 'justified']);
export const ReferenceActUnderlineSchema = z.enum(['none', 'single', 'double', 'dotted']);
/**
 * Font colour as an `#RRGGBB` hex string. Word stores `<w:color w:val="RRGGBB"/>`
 * without the leading `#` (and never emits it for `auto`); the extractor prepends
 * the `#` so the value can flow straight into Office.js `Font.color` / the cabinet
 * charte without a second conversion (Word-style exhaustiveness, 2026-07-12).
 */
export const ReferenceActColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/);

const ReferenceActFontSizeSchema = z.number().finite().positive();
const ReferenceActSpacingSchema = z.number().finite().nonnegative();
const ReferenceActLineSpacingSchema = z.number().finite().positive();
// Indents may be negative in Word (outdents / hanging), so `.finite()` without a
// sign constraint — unlike spacing which is non-negative.
const ReferenceActIndentSchema = z.number().finite();

/**
 * Sparse subset of a heading or piece style that the cabinet editor can apply
 * without replacing settings outside the reference-act import scope.
 */
export const ReferenceActInlineStylePatchSchema = z.object({
  fontSize: ReferenceActFontSizeSchema.optional(),
  // Word-style exhaustiveness (2026-07-12): colour + left/right indent are the
  // distinctive, end-to-end-wired additions (charte → add-in `font.color`/`pf.*Indent`).
  color: ReferenceActColorSchema.optional(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  underline: ReferenceActUnderlineSchema.optional(),
  alignment: ReferenceActTextAlignmentSchema.optional(),
  spaceBefore: ReferenceActSpacingSchema.optional(),
  spaceAfter: ReferenceActSpacingSchema.optional(),
  leftIndent: ReferenceActIndentSchema.optional(),
  rightIndent: ReferenceActIndentSchema.optional(),
}).strict().refine(
  (patch) => Object.keys(patch).length > 0,
  'Reference act inline style patch must contain at least one supported property.',
);

/** Sparse subset of the cabinet normal-paragraph settings. */
export const ReferenceActParagraphStylePatchSchema = z.object({
  fontSize: ReferenceActFontSizeSchema.optional(),
  // Word-style exhaustiveness (2026-07-12): colour + left/right indent, end-to-end
  // wired to the charte + add-in body style.
  color: ReferenceActColorSchema.optional(),
  lineSpacing: ReferenceActLineSpacingSchema.optional(),
  spaceAfter: ReferenceActSpacingSchema.optional(),
  alignment: ReferenceActTextAlignmentSchema.optional(),
  firstLineIndent: ReferenceActSpacingSchema.optional(),
  leftIndent: ReferenceActIndentSchema.optional(),
  rightIndent: ReferenceActIndentSchema.optional(),
}).strict().refine(
  (patch) => Object.keys(patch).length > 0,
  'Reference act paragraph style patch must contain at least one supported property.',
);

const ReferenceActHeadingsPatchSchema = z.object({
  h1: ReferenceActInlineStylePatchSchema.optional(),
  h2: ReferenceActInlineStylePatchSchema.optional(),
  h3: ReferenceActInlineStylePatchSchema.optional(),
  h4: ReferenceActInlineStylePatchSchema.optional(),
}).strict().refine(
  (patch) => Object.keys(patch).length > 0,
  'Reference act heading patch must contain at least one editable heading.',
);

const ReferenceActPieceMarkersPatchSchema = z.object({
  pieceBeforeBordereau: ReferenceActInlineStylePatchSchema.optional(),
  pieceAfterBordereau: ReferenceActInlineStylePatchSchema.optional(),
}).strict().refine(
  (patch) => Object.keys(patch).length > 0,
  'Reference act marker patch must contain at least one editable piece marker.',
);

/**
 * Formatting inferred from a reference DOCX. The schema intentionally exposes
 * only the seven styles editable by the cabinet formatting UI; this prevents
 * a reference act from mutating unrelated marker, quote, or page-break rules.
 */
export const ReferenceActFormattingPatchSchema = z.object({
  fontName: z.string().trim().min(1).max(256).optional(),
  paragraph: ReferenceActParagraphStylePatchSchema.optional(),
  headings: ReferenceActHeadingsPatchSchema.optional(),
  markers: ReferenceActPieceMarkersPatchSchema.optional(),
}).strict();

export const ActStructureContractSchema = z.object({
  headingLevels: z.array(
    z.object({
      role: z.string().min(1),
      hectorTag: HectorTagSchema,
      examples: z.array(z.string().min(1)).max(12),
    }),
  ).min(1),
  numbering: z.record(z.string(), z.string()).default({}),
  pieceCitation: z.object({ examples: z.array(z.string()).default([]) }).default({ examples: [] }),
}).passthrough();

export const ActStructurePreferenceSchema = z.object({
  user_id: z.string().uuid(),
  cabinet_id: z.string().uuid(),
  structure_markdown: z.string().min(1),
  structure_contract: ActStructureContractSchema,
  structure_prompt_instructions: z.string().min(1),
  structure_preview_sample: z.record(z.string(), z.unknown()).default({}),
  source: ActStructureSourceSchema,
  version: z.number().int().positive(),
  source_hash: z.string().min(8),
});

export const ActStructureSourceResponseSchema = z.object({
  structure_markdown: z.string().min(1),
  structure_contract: ActStructureContractSchema,
  structure_prompt_instructions: z.string().min(1),
  structure_preview_sample: z.record(z.string(), z.unknown()).default({}),
});

export const ActPersonalizationSourceResponseSchema = z.object({
  redactionStyle: RedactionStyleBlockSchema.nullable(),
  structureSource: ActStructureSourceResponseSchema.nullable(),
});

export const ActStructurePatchRowSchema = z.object({
  user_id: z.string().uuid(),
  cabinet_id: z.string().uuid(),
  doc_type: z.string().min(1),
  document_template_patch: z.string().nullable().optional(),
  compile_status: CompileStatusSchema,
  compile_error: z.string().nullable().optional(),
  baseline_prompt_hash: z.string().nullable().optional(),
  source_version: z.number().int().positive(),
  source_hash: z.string().min(8),
  compiler_version: z.string().min(1),
  compile_job_id: z.string().nullable().optional(),
  locked_at: z.string().nullable().optional(),
  lease_expires_at: z.string().nullable().optional(),
  attempt_count: z.number().int().min(0),
  compiled_at: z.string().nullable().optional(),
}).superRefine((row, ctx) => {
  const hasPatch = typeof row.document_template_patch === 'string' && row.document_template_patch.trim().length > 0;

  if (row.compile_status === 'completed' && !hasPatch) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'completed patch requires document_template_patch',
    });
  }

  if (row.compile_status !== 'completed' && hasPatch) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'non-completed patch must not expose reusable patch content',
    });
  }

  if (row.compile_status === 'failed' && !row.compile_error) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'failed patch requires sanitized compile_error',
    });
  }
});

export const PatchStateSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('none'), usable: z.literal(false), patch: z.null() }),
  z.object({ status: z.literal('missing'), usable: z.literal(false), patch: z.null() }),
  z.object({ status: z.literal('compiling'), usable: z.literal(false), patch: z.null() }),
  z.object({ status: z.literal('failed'), usable: z.literal(false), patch: z.null() }),
  z.object({ status: z.literal('completed'), usable: z.literal(true), patch: z.string().min(1) }),
]);

export const PreGenerationStatusSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('act_structure_patch_compile_started'),
    jobId: z.string().min(1),
    invocationId: z.string().min(1),
  }),
  z.object({
    type: z.literal('act_structure_patch_compile_completed'),
    jobId: z.string().min(1),
    invocationId: z.string().min(1),
  }),
  z.object({
    type: z.literal('act_structure_patch_compile_failed'),
    jobId: z.string().min(1),
    invocationId: z.string().min(1),
    actions: z.tuple([
      z.literal('Reessayer'),
      z.literal('Faire sans le patch'),
    ]),
  }),
]);

export const ActReferenceAnalysisResultSchema = z.object({
  // STYLE.md is persisted as a bounded memory block; keep the shared contract aligned
  // with the existing ~5000-token / 20k-char envelope so oversized uploads are rejected early.
  styleMarkdown: z.string().min(1).max(REDACTION_STYLE_CHAR_LIMIT),
  structureMarkdown: z.string().min(1),
  structureContract: ActStructureContractSchema,
  structurePromptInstructions: z.string().min(1),
  previewSample: z.record(z.string(), z.unknown()).default({}),
});

// ─── Template Library — métadonnées de récupération (2026-07-12) ────────────
//
// Extraites d'un acte de référence (template_pack) pour rendre la bibliothèque de
// modèles cabinet-partagée INTERROGEABLE par les agents (case-discovery-v3 /
// edition-v3) et sélectionnable à la génération. Distinct du STYLE.md (plume) et du
// structure_contract (numérotation) : ici c'est de la SÉMANTIQUE de récupération
// (de quoi parle l'acte), pas du style. Anonymisation : parties par rôle +
// descripteur, jamais de PII gratuite (cohérent « imite la forme, jamais le contenu »).
// `procedure_type` = la MATIÈRE que l'avocat choisit en premier dans l'add-in Word.
// Ces 7 valeurs sont EXACTEMENT `legalCategories` de l'add-in
// (apps/addin-word/src/taskpane/components/hector-ai/HectorAI.tsx) — elles DOIVENT
// rester en sync (labels : Droit Social / Droit de la Famille / Droit Commercial /
// Droit Civil / Procédures devant le Juge de l'exécution / Droit Pénal / Droit Public).
export const TemplateProcedureTypeSchema = z.enum([
  'social', 'famille', 'commercial', 'civil', 'jex', 'penal', 'public',
]);

/**
 * État de l'extraction async des métadonnées d'un template_pack (backing la
 * colonne `template_packs.metadata_status`, contrainte CHECK côté DB). Machine
 * d'états : `missing` (jamais lancé) → `extracting` (en vol) → `completed`
 * (métadonnées présentes) | `failed` (échec doux, `metadata_error` renseigné).
 * Le front poll la liste tant qu'un pack est `extracting`.
 */
export const TemplateMetadataStatusSchema = z.enum([
  'missing', 'extracting', 'completed', 'failed',
]);

export const TemplatePartySchema = z.object({
  /** demandeur | défendeur | appelant | intimé | prévenu | requérant … */
  role: z.string().min(1),
  /** Descripteur anonymisé : « la société défenderesse », « le salarié ». Jamais de PII. */
  descriptor: z.string().min(1),
});

/**
 * Un MOYEN de l'acte-modèle : un argument juridique, avec ses fondements (articles)
 * et sa JP citée. Un moyen JUSTIFIE une prétention (cf. `TemplatePretentionSchema`).
 * Nommage aligné sur `RipostePoint.deja_mobilise` (plan-detaille-v2/riposte-plan.ts).
 * Les articles n'ont d'importance qu'À TRAVERS les moyens — pas de liste globale.
 */
export const TemplateMoyenSchema = z.object({
  /** Intitulé du moyen, ex. « Sur l'absence de novation ». */
  intitule: z.string().min(1),
  /** Articles / textes invoqués au soutien du moyen (ex. « article 1240 du Code civil »). */
  fondements: z.array(z.string()).default([]),
  /** Jurisprudence citée au soutien du moyen (ex. « Cass. civ. 2e, 26/03/2026 »). */
  jurisprudence: z.array(z.string()).default([]),
});

/**
 * Une PRÉTENTION de l'acte-modèle : une demande (à titre principal / subsidiaire /
 * accessoire…) et les MOYENS qui la justifient. Reproduit la hiérarchie du sommaire
 * d'un acte (prétention → moyens). Remplace l'ancien couple PLAT
 * `demande_or_outcome` (string) + `moyens[]` : les moyens vivent désormais SOUS la
 * prétention qu'ils soutiennent, dans une seule section « Prétentions & Moyens ».
 */
export const TemplatePretentionSchema = z.object({
  /** Libellé de la prétention, ex. « À titre principal, débouter la demanderesse… ». */
  intitule: z.string().min(1),
  /** Les moyens qui justifient CETTE prétention, dans l'ordre du document. */
  moyens: z.array(TemplateMoyenSchema).default([]),
});

export const TemplateMetadataSchema = z.object({
  /** Type d'acte, ex. « Conclusions en défense », « Assignation ». */
  act_type: z.string().min(1),
  /** Matière (add-in Word), voir `TemplateProcedureTypeSchema`. */
  procedure_type: TemplateProcedureTypeSchema,
  /** « Conseil de Prud'hommes », « Cour d'appel », « Tribunal correctionnel »… */
  jurisdiction: z.string(),
  /** Résumé des faits — l'incident / le litige, factuel. */
  facts_summary: z.string(),
  parties: z.array(TemplatePartySchema).default([]),
  /**
   * Les prétentions de l'acte, chacune avec les moyens qui la justifient (et, par
   * moyen, ses fondements + JP). Reproduit la structure « Prétentions & Moyens » du
   * sommaire. Remplace l'ancien couple `demande_or_outcome` + `moyens[]` plat.
   */
  pretentions: z.array(TemplatePretentionSchema).default([]),
});
// NB: default `.strip()` (NOT `.passthrough()`) at EVERY level (moyen, prétention,
// metadata) — a stray key from the LLM is dropped, not kept (safer output posture, cf.
// shared-types CLAUDE.md `.strict()` incident). `.passthrough()` would also inject a
// `& { [k: string]: unknown }` index signature into `z.infer`, collapsing react-hook-form's
// `FieldArrayPath` to `never` in the frontend correction form — which now uses NESTED
// field arrays (`useFieldArray('pretentions')` + `useFieldArray('pretentions.${i}.moyens')`).

// ── Letterhead (en-tête cabinet) — Lot B, 2026-07-26 ; en-tête réel 2026-08-17 ─
// Cabinet letterhead persisted in `cabinet_settings.letterhead_settings` (JSONB). The
// image binary lives on OVH S3; only its private key + the text fields + render flags
// live in the JSONB.
//
// Rendering at act generation is PLATFORM-DEPENDENT (add-in `applyLetterhead.ts`):
//   • recent Word Desktop → the REAL Word first-page header ;
//   • Word on the web, Office LTSC/volume-licensed, or a document that already carries a
//     header → a body block at the TOP OF PAGE 1 (the historical behaviour).
// The 2026-07-26 decision ("a true first-page-only header isn't achievable via Office.js")
// was right on its blocking fact but generalised too far: WRITING into
// `Section.getHeader('FirstPage')` is WordApi 1.1 and works everywhere; only ACTIVATING
// "different first page" (`PageSetup.differentFirstPageHeaderFooter`) is WordApiDesktop 1.3.
//
// `firstPageOnly` (below) stays INERT and has no UI toggle: both paths are already
// first-page-only, so the flag has nothing to switch.

/** Alignment of the letterhead block at the top of page 1 (no `justified` — a compact identity block). */
export const LetterheadAlignmentSchema = z.enum(['left', 'center', 'right']);

/**
 * MIME types a lawyer may DEPOSIT as a letterhead. Single source of truth mirrored by the
 * upload route (`api.routes.ts`) and the web drop zones (`letterheadUpload.ts`, consumed by
 * `LetterheadsManager`) — the two used to hold hand-copied literals that could drift apart
 * silently.
 *
 * PDF is accepted at the DOOR only: the server rasterises page 1 and stores a PNG (see
 * {@link LetterheadImageSchema}). Nothing downstream ever sees a PDF.
 */
export const LETTERHEAD_UPLOAD_MIME_TYPES = ['image/png', 'image/jpeg', 'application/pdf'] as const;

/**
 * Uploaded letterhead image. `filePath` is a PRIVATE S3 key and is NEVER exposed to the
 * client — the API returns a short-lived signed URL (`LetterheadResponse.imageUrl`) instead.
 *
 * `mimeType` is deliberately NARROWER than {@link LETTERHEAD_UPLOAD_MIME_TYPES}: the STORED
 * artefact is always raster. A deposited PDF is rasterised to PNG at upload, because the three
 * renderers cannot embed a PDF page and would each fail differently and SILENTLY:
 *   • add-in Word — `insertInlinePictureFromBase64` only takes image bytes (`applyLetterhead.ts`) ;
 *   • bordereau .docx — `docx` `ImageRun` accepts png|jpg|gif|bmp only (`bordereauCoverDocxExport.ts`) ;
 *   • bordereau PDF — `pdf-lib` `embedPdf` exists, but its output is a page, not an image, and
 *     its dimensions are in POINTS while the shared fitter expects PIXELS.
 * All three paths are fail-soft, so an un-rasterised PDF would produce acts and bordereaux
 * with NO letterhead and nothing but a `console.warn` — the worst outcome for a filed document.
 */
export const LetterheadImageSchema = z.object({
  filePath: z.string().min(1),
  mimeType: z.enum(['image/png', 'image/jpeg']),
  widthMm: z.number().finite().positive().optional(),
});

/**
 * MIME type of a cabinet PAPER: the lawyer's own Word document, kept for its header, footer
 * and page margins. Distinct from {@link LETTERHEAD_UPLOAD_MIME_TYPES}, which stays the list
 * for the LOGO image: the two deposits feed different renderers and must not be merged.
 */
export const LETTERHEAD_DOCUMENT_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document' as const;

/**
 * Largest paper accepted. The Word add-in loads it with `Document.insertFileFromBase64`,
 * which rejects a document above 4 MB, so a bigger paper would work in the canvas and fail
 * silently in Word.
 * Source: https://learn.microsoft.com/en-us/javascript/api/word/word.document (verified 2026-10-11)
 */
export const LETTERHEAD_DOCUMENT_MAX_BYTES = 4 * 1024 * 1024;

/**
 * What the deposit noticed about a paper without refusing it.
 * `unrenderable_image`: a header or footer relies on an EMF/WMF picture, which browsers do not
 * draw. The canvas shows a blank where Word shows the picture; the exported file keeps it.
 */
export const LetterheadDocumentWarningSchema = z.enum(['unrenderable_image']);

/**
 * A cabinet paper as stored. `filePath` is a PRIVATE S3 key, never sent to a client: bytes are
 * served by a same-origin route, like the logo. The stored file has an EMPTY body (one empty
 * paragraph) and keeps the source document's headers, footers and section properties.
 */
export const LetterheadDocumentSchema = z.object({
  filePath: z.string().min(1),
  originalFileName: z.string().min(1),
  sizeBytes: z.number().int().positive(),
  warnings: z.array(LetterheadDocumentWarningSchema).default([]),
});

/**
 * Structured letterhead text — cabinet identity rendered as a typographic header block.
 *
 * LEGACY, READ-ONLY since 2026-08-17. The letterhead is now a DEPOSITED FILE and nothing else:
 * the seven text inputs were removed from the editor, so no new value can ever be produced.
 * The schema keeps the shape for READS (write-strict / read-lax, the pattern already used for
 * `feature_flags` in `auth/index.ts`) because rows persisted before that date still carry text
 * and the add-in still renders it — dropping it here would silently empty the letterhead of a
 * cabinet whose header is text-ONLY, on documents filed with a court.
 *
 * Consequently this shape is ABSENT from {@link LetterheadSettingsPatchSchema}: the server
 * re-attaches the stored value on every PATCH, exactly as it already does for `image`.
 */
export const LetterheadTextFieldsSchema = z.object({
  cabinetName: z.string().max(200).optional(),
  addressLines: z.array(z.string().max(200)).max(6).optional(),
  barreau: z.string().max(120).optional(),
  phone: z.string().max(60).optional(),
  email: z.string().max(160).optional(),
  website: z.string().max(200).optional(),
  extra: z.string().max(300).optional(),
});

/**
 * Complete letterhead config for a cabinet — ONE object per cabinet. Rendering is driven by
 * presence: image, plus any LEGACY text (see {@link LetterheadTextFieldsSchema}); `null` =
 * that block is absent; `enabled: false` = no header at all (the stored image asset is kept).
 *
 * This is the READ shape — it is what the DB row parses into, hence the tolerated `text`.
 */
export const LetterheadSettingsSchema = z.object({
  enabled: z.boolean(),
  image: LetterheadImageSchema.nullable(),
  /**
   * Clé ABSENTE tolérée, et c'est délibéré (2026-08-17). `text` est un champ LEGACY dont
   * l'écran n'est plus producteur : le PATCH ne peut plus le porter, donc une ligne écrite
   * après ce retrait peut très bien ne pas avoir la clé du tout. En l'exigeant, le schéma de
   * LECTURE rejetait la ligne entière — `parseActiveLetterheadSettings` rendait `null` et la
   * page de garde PDF sortait SANS logo, alors que `enabled` valait `true` et que l'image
   * existait. Symptôme d'autant plus retors que le front, qui ne parse pas, affichait
   * l'en-tête correctement : seul l'export serveur perdait le logo.
   *
   * `.default(null)` plutôt que `.optional()` : l'entrée devient facultative mais la SORTIE
   * reste `LetterheadTextFields | null`, donc aucun consommateur n'a à gérer `undefined`.
   *
   * C'est le pattern « write strict / read relâché » déjà en vigueur sur les feature flags
   * (cf. `packages/shared-types/CLAUDE.md`) : durcir un schéma de lecture casse le PARSING
   * des lignes déjà en base, pas seulement les écritures futures.
   */
  text: LetterheadTextFieldsSchema.nullable().default(null),
  alignment: LetterheadAlignmentSchema,
  firstPageOnly: z.boolean(),
});

/**
 * PATCH body: render flags only. TWO sub-objects are server-owned and therefore absent here,
 * so a client can never erase them:
 *   • `image` — the BINARY flows through the dedicated upload/delete routes ;
 *   • `text`  — LEGACY and no longer producible (see {@link LetterheadTextFieldsSchema}).
 *
 * Why `text` must be absent rather than merely optional: an editor that no longer holds the
 * text would send `text: null` and erase a stored letterhead without an error, a log, or a
 * broken build — the exact failure already suffered on the bordereau labels
 * (cf. `components/CLAUDE.md`). Since 2026-09-03 (migration 163) the handler writes to
 * `cabinet_letterheads` field by field rather than replacing a whole JSONB column, which
 * removes the mechanism of that erasure — but not the reason to keep the key out of the wire
 * contract: what a client cannot say, it cannot get wrong.
 *
 * The one image knob that DOES ride this PATCH is its DISPLAY WIDTH: `imageWidthMm` is a
 * lawyer-settable layout value living inside the stored `image` sub-object (there is no
 * image-metadata endpoint). The handler merges it into the server-owned image. Semantics:
 *   • a positive number → set the logo width in mm (rendered by the add-in in `applyLetterhead.ts`,
 *     `writeLetterheadInto` — same value on the header path and the body path),
 *   • `null` → reset to the image's native size (strip stored `widthMm`),
 *   • key omitted (`undefined`) → leave the stored width unchanged.
 * Cap 190mm ≈ the A4 page-width ceiling (210mm) minus a hair — a wider logo is nonsensical.
 */
export const LetterheadSettingsPatchSchema = z.object({
  enabled: z.boolean(),
  alignment: LetterheadAlignmentSchema,
  firstPageOnly: z.boolean(),
  imageWidthMm: z.number().finite().positive().max(190).nullable().optional(),
});

/** GET response — settings + a short-lived signed URL for the image (null if no image). */
export const LetterheadResponseSchema = z.object({
  settings: LetterheadSettingsSchema.nullable(),
  imageUrl: z.string().nullable(),
  /**
   * True when the DEFAULT letterhead carries a paper. `.default(false)` so a client newer than
   * the backend it talks to reads "no paper" and keeps today's behaviour during a rollout.
   */
  hasDocument: z.boolean().default(false),
  /**
   * Changes every time the default letterhead's paper is deposited again; `null` without a
   * paper. A client that caches the paper bytes (the Word add-in) compares it to know the
   * lawyer replaced the file. Opaque: never the S3 key.
   */
  documentVersion: z.string().nullable().default(null),
});

/** Starting point for a cabinet that never configured a letterhead (disabled, empty). */
export const DEFAULT_LETTERHEAD_SETTINGS: LetterheadSettings = {
  enabled: false,
  image: null,
  text: null,
  alignment: 'left',
  firstPageOnly: true,
};

export type RedactionStyleLabel = z.infer<typeof RedactionStyleLabelSchema>;
export type RedactionStyleBlock = z.infer<typeof RedactionStyleBlockSchema>;
export type ActStructurePatchReviewAction = z.infer<typeof ActStructurePatchReviewActionSchema>;
export type HectorTag = z.infer<typeof HectorTagSchema>;
export type ActStructureSource = z.infer<typeof ActStructureSourceSchema>;
export type CompileStatus = z.infer<typeof CompileStatusSchema>;
export type ReferenceActTextAlignment = z.infer<typeof ReferenceActTextAlignmentSchema>;
export type ReferenceActUnderline = z.infer<typeof ReferenceActUnderlineSchema>;
export type ReferenceActColor = z.infer<typeof ReferenceActColorSchema>;
export type TemplateProcedureType = z.infer<typeof TemplateProcedureTypeSchema>;
export type TemplateMetadataStatus = z.infer<typeof TemplateMetadataStatusSchema>;
export type TemplateParty = z.infer<typeof TemplatePartySchema>;
export type TemplateMoyen = z.infer<typeof TemplateMoyenSchema>;
export type TemplatePretention = z.infer<typeof TemplatePretentionSchema>;
export type TemplateMetadata = z.infer<typeof TemplateMetadataSchema>;
export type ReferenceActInlineStylePatch = z.infer<typeof ReferenceActInlineStylePatchSchema>;
export type ReferenceActParagraphStylePatch = z.infer<typeof ReferenceActParagraphStylePatchSchema>;
export type ReferenceActFormattingPatch = z.infer<typeof ReferenceActFormattingPatchSchema>;
export type ActStructureContract = z.infer<typeof ActStructureContractSchema>;
export type ActStructurePreference = z.infer<typeof ActStructurePreferenceSchema>;
export type ActStructureSourceResponse = z.infer<typeof ActStructureSourceResponseSchema>;
export type ActPersonalizationSourceResponse = z.infer<typeof ActPersonalizationSourceResponseSchema>;
export type ActStructurePatchRow = z.infer<typeof ActStructurePatchRowSchema>;
export type PatchState = z.infer<typeof PatchStateSchema>;
export type PreGenerationStatus = z.infer<typeof PreGenerationStatusSchema>;
export type LetterheadAlignment = z.infer<typeof LetterheadAlignmentSchema>;
export type LetterheadImage = z.infer<typeof LetterheadImageSchema>;
export type LetterheadDocument = z.infer<typeof LetterheadDocumentSchema>;
export type LetterheadDocumentWarning = z.infer<typeof LetterheadDocumentWarningSchema>;
export type LetterheadTextFields = z.infer<typeof LetterheadTextFieldsSchema>;
export type LetterheadSettings = z.infer<typeof LetterheadSettingsSchema>;
export type LetterheadSettingsPatch = z.infer<typeof LetterheadSettingsPatchSchema>;
export type LetterheadResponse = z.infer<typeof LetterheadResponseSchema>;
export type ActReferenceAnalysisResult = z.infer<typeof ActReferenceAnalysisResultSchema>;
